import useSWR, { SWRResponse } from "swr";

import { useSWRApiErrorHandler } from "../common/hooks";
import { CreateNewProfileFormValues } from "../features/EntityManagement/components/CreateNewProfileForm";
import { UpdateProfileFormValues } from "../features/EntityManagement/components/UpdateProfileForm";

import request, { ApiError } from "./common";
import { ProfileType } from "../features/EntityManagement/ProfileManageMentView";
import { ResponseWithPagination } from "../types/common";

const profileApi = {
    create: (values: CreateNewProfileFormValues): Promise<UserProfile> => {
        const payload: UserProfilePayload = {
            name: values.entityName,
            type: values.entityType,
            location: values.entityCountryCity,
            contactPersonName: values.contactPersonName,
            contactPersonNumber: values.contactPersonNumber,
            contactPersonEmail: values.contactPersonEmail,
            accountName: "",
            wallet: "",
        };
        return request<UserProfile, UserProfilePayload>(
            "/user-profile",
            "POST",
            payload,
        );
    },
    update: (
        id: number,
        values: UpdateProfileFormValues,
    ): Promise<UserProfile> => {
        const payload: UserProfilePayload = {
            name: values.profileName,
            type: values.profileType,
            location: values.profileCountryCity,
            contactPersonName: values.contactPersonName,
            contactPersonNumber: values.contactPersonNumber,
            contactPersonEmail: values.contactPersonEmail,
            accountName: "",
            wallet: "",
        };
        return request<UserProfile, UserProfilePayload>(
            `/user-profile/id/${id}`,
            "PUT",
            payload,
        );
    },
    profileListPaging: (page: number, size: number): Promise<ResponseWithPagination<UserProfile[]>> => {
        return request<ResponseWithPagination<UserProfile[]>, null>(
            `/user-profile/paging?page=${page}&size=${size}`, "GET", null
        );
    },
    profileListPagingByType: (type: string | undefined, page: number, size: number): Promise<ResponseWithPagination<UserProfile[]>> => {
        return request<ResponseWithPagination<UserProfile[]>, null>(
            `/user-profile/typePaging/${type}?page=${page}&size=${size}`, "GET", null
        );
    }
};

export const useProfileList = (): SWRResponse<UserProfile[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<UserProfile[], ApiError>("/user-profile", request, {
        onError,
    });
};

export const useProfileListByType = (
    type: string | undefined,
): SWRResponse<UserProfile[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<UserProfile[], ApiError>(
        () => (type ? `/user-profile/type/${type}` : null),
        request,
        { onError },
    );
};

export const useUserProfile = (
    id: number,
): SWRResponse<UserProfile, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<UserProfile, ApiError>(`/user-profile/id/${id}`, request, {
        onError,
    });
};

export const useProfileTypeList = (): SWRResponse<ProfileType[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<ProfileType[], ApiError>("user-profile/type", request, {
        onError,
    });
};

export const useProfileListByRole = (): SWRResponse<UserProfile[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<UserProfile[], ApiError>("/user-profile/getProfileNameByRole", request, {
        onError,
    });
};


export type UserProfilePayload = {
    contactPersonEmail: string;
    contactPersonName: string;
    contactPersonNumber: string;
    location: string;
    name: string;
    type: string;
    accountName?: string;
    wallet?: string;
};

export type UserProfile = {
    id: number;
    contactPersonEmail: string;
    contactPersonName: string;
    contactPersonNumber: string;
    location: string;
    name: string;
    type: string;
    accountName: string;
    wallet: string;
};



export enum NodeGroup {
    DEVELOPERS = "developers",
    MAIN_CONTRACTORS = "main-contractors",
}
export default profileApi;
